"use client";

import { ArrowUpRight, MoreHorizontal, StarOff, Trash2, Link as LinkIcon, Star } from "lucide-react";

import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
    SidebarGroup,
    SidebarGroupLabel,
    SidebarMenu,
    SidebarMenuAction,
    SidebarMenuButton,
    SidebarMenuItem,
    useSidebar,
} from "@/components/ui/sidebar";
import { Skeleton } from "./ui/skeleton";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useDeleteThreadMutation, useUpdateThreadMutation } from "@/api/mutations/ThreadMutations";
import { toast } from "sonner";
import { useState } from "react";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";

export function NavChats({
    chats,
    isLoading,
    isFetchingNextPage,
    title,
    hideShowMore,
    bottomRef,
    disableHighlight,
    fetchNextPage,
}: {
    chats: {
        id: string;
        name: string;
        url: string;
        emoji?: string;
    }[];
    isLoading: boolean;
    isFetchingNextPage: boolean;
    title: string;
    hideShowMore?: boolean;
    bottomRef?: React.RefObject<HTMLDivElement>;
    disableHighlight?: boolean;
    fetchNextPage?: () => void;
}) {
    const location = useLocation();
    const pathname = location.pathname;
    const { isMobile } = useSidebar();
    const { t } = useTranslation();
    const [isExpanded, setIsExpanded] = useState(false);
    const navigate = useNavigate();
    const { mutate: updateThread } = useUpdateThreadMutation();
    const { mutateAsync: deleteThread } = useDeleteThreadMutation();

    const onDeleteThread = async (id: string) => {
        await deleteThread([id]);
        if (pathname.includes(id)) {
            navigate("/");
        }
    }

    const handleCopyLink = (url: string) => {
        const link = `${window.location.origin}/${url}`
        navigator.clipboard.writeText(link);
        toast.success(t("chat.linkCopied"))
    };

    const handleOpenInNewTab = (url: string) => {
        const link = `${window.location.origin}/${url}`
        window.open(link, "_blank")
    };

    // For favorites, show only 3 items unless expanded
    const isFavorites = title === t("chat.favorites");
    const shouldLimitItems = isFavorites && chats.length > 3 && !isExpanded;
    const displayedChats = shouldLimitItems ? chats.slice(0, 3) : chats;

    if (isLoading) {
        return (
            <SidebarGroup className="flex w-full flex-col gap-2 p-2 group-data-[collapsible=icon]:hidden">
                <SidebarGroupLabel>{title}</SidebarGroupLabel>
                {Array(5)
                    .fill(0)
                    .map((_, i) => (
                        <div className="flex flex-col gap-2 rounded-lg border p-1" key={i}>
                            <Skeleton className=" h-4 w-full" />
                        </div>
                    ))}
            </SidebarGroup>
        );
    }

    return (
        <>
            <SidebarGroup className={cn("group-data-[collapsible=icon]:hidden", isFavorites ? "pb-0" : "")}>
                <SidebarGroupLabel>{title}</SidebarGroupLabel>
                <SidebarMenu>
                    {displayedChats.map((item, index) => (
                        <SidebarMenuItem key={index}>
                            <SidebarMenuButton
                                asChild
                                isActive={disableHighlight ? false : pathname.includes(item.url)}
                            >
                                <Link to={item.url}>
                                    {item.emoji && <span>{item.emoji}</span>}
                                    <span>{item.name}</span>
                                </Link>
                            </SidebarMenuButton>
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <SidebarMenuAction showOnHover>
                                        <MoreHorizontal />
                                        <span className="sr-only">{t("chat.more")}</span>
                                    </SidebarMenuAction>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent
                                    className="w-56 rounded-lg"
                                    side={isMobile ? "bottom" : "right"}
                                    align={isMobile ? "end" : "start"}
                                >
                                    <DropdownMenuItem
                                        onClick={() =>
                                            updateThread({
                                                data: { isFavorite: !isFavorites },
                                                id: item.id,
                                            })
                                        }
                                    >
                                        {!isFavorites ? <Star /> : <StarOff className="text-muted-foreground" />}
                                        <span>{!isFavorites ? t("chat.addToFavorites") : t("chat.removeFromFavorites")}</span>
                                    </DropdownMenuItem>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem onClick={() => handleCopyLink(item.url)}>
                                        <LinkIcon className="text-muted-foreground" />
                                        <span>{t("chat.copyLink")}</span>
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => handleOpenInNewTab(item.url)}>
                                        <ArrowUpRight className="text-muted-foreground"/>
                                        <span>{t("chat.openInNewTab")}</span>
                                    </DropdownMenuItem>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem onClick={() => onDeleteThread(item.id)}>
                                        <Trash2 className="text-muted-foreground" />
                                        <span>{t("common.delete")}</span>
                                    </DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>
                        </SidebarMenuItem>
                    ))}
                    {isFetchingNextPage && (
                        <div className="flex flex-col gap-2 p-2">
                            {Array(3)
                                .fill(0)
                                .map((_, i) => (
                                    <div className="flex flex-col gap-2 rounded-lg border p-1" key={i}>
                                        <Skeleton className=" h-4 w-full" />
                                    </div>
                                ))}
                        </div>
                    )}
                    {isFavorites && chats.length > 3 && (
                        <SidebarMenuItem onClick={() => setIsExpanded(!isExpanded)}>
                            <SidebarMenuButton className="text-sidebar-foreground/70">
                                <MoreHorizontal />
                                <span>{isExpanded ? t("chat.showLess") : t("chat.showMore", { count: chats.length - 3 })}</span>
                            </SidebarMenuButton>
                        </SidebarMenuItem>
                    )}
                    {!hideShowMore && !isFavorites && (
                        <SidebarMenuItem onClick={fetchNextPage}>
                            <SidebarMenuButton className="text-sidebar-foreground/70">
                                <MoreHorizontal />
                                <span>{t("chat.more")}</span>
                            </SidebarMenuButton>
                        </SidebarMenuItem>
                    )}
                </SidebarMenu>
            </SidebarGroup>
            <div ref={bottomRef} style={{ height: 1 }} />
        </>
    );
}
